// Library
import { ClassAttributes, InputHTMLAttributes, useMemo } from 'react';
import { FieldHookConfig, useField } from 'formik';
import { isEmpty } from 'lodash';

import { convertDateAndTimeToDisplayFormat } from '@/utils';

// CSS Style for this module
import styles from './dateFieldFormik.module.scss';

interface InputProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * label of the date field element
	 */
	label?: string;
	/**
	 * type of date field - date or datetime-local
	 */
	type?: string;
	props: InputHTMLAttributes<HTMLInputElement> &
		ClassAttributes<HTMLInputElement> &
		FieldHookConfig<string>;
	/**
	 * Preset size of the input element. Auto is the default size
	 */
	size?: 'small' | 'auto';
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * Date field let users enter and edit a date.
 * @param {InputProps} Prop
 * @returns  a styled date field element
 */
export const DateFieldFormik = ({
	customStyles,
	label,
	type,
	size = 'auto',
	testId,
	props,
}: InputProps) => {
	const [field, meta] = useField(props);
	const hasError = useMemo(() => meta.error && meta.touched, [meta.error, meta.touched]);
	const inputStyles = useMemo(
		() =>
			[
				styles.dateField,
				styles[`dateField-size-${!props.disabled ? size : ''}`],
				styles[`dateField-disabled-${props.disabled}`],
				styles[`dateField-${hasError ? 'error' : ''}`],
			].join(' '),
		[hasError, props.disabled, size],
	);

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			{label && <label htmlFor={field.name}>{label}</label>}
			{!props.disabled ? (
				<input
					type={type ? type : 'date'}
					className={inputStyles}
					{...field}
					{...props}
					data-testid={testId}
				/>
			) : !isEmpty(field.value) ? (
				<p className={inputStyles}>{convertDateAndTimeToDisplayFormat(field.value, 'date')}</p>
			) : (
				<p className={inputStyles}>&nbsp;</p>
			)}
			{hasError && <p className={styles['error-msg']}>{meta.error}</p>}
		</div>
	);
};
